import { GRAY_200, GRAY_300, GRAY_500, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'

import Box from '@mui/material/Box'
import { ArrowBack, ChatBubbleOutline } from '@mui/icons-material'
import { CloseConversationMenu } from '../CloseConversationMenu'
import { ConversationThread } from '../ConversationThread/ConversationThread'
import { EmptyContent } from 'components/common/EmptyContent'
import { FC } from 'react'
import IconButton from '@mui/material/IconButton'
import { MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { ConversationStatus, ConversationType, PresenceStatus } from 'models/conversations'
import Stack from '@mui/material/Stack'
import { SupportHoursSummary } from '../SupportHours/SupportHoursSummary'
import Typography from '@mui/material/Typography'
import { getThreadHeaderTitle } from '../helpers/conversationDisplay'
import { useChatWidget } from '../ChatWidget.context'
import { useConversationSocket } from 'utils/conversationSocket'
import { useGetConversations } from 'dataQueries/conversations.query'
import { useTranslation } from 'react-i18next'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useUserData } from 'components/contexts/UserDataContext'

/** Renders the selected conversation's history + composer inside the expanded drawer. */
export const ConversationThreadPanel: FC = () => {
  const { t } = useTranslation(['chat'])
  const { selectedConversationId, setActiveConversation } = useChatWidget()
  const isMobile = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { baseUserData } = useUserData()
  const { presence } = useConversationSocket()
  const currentUserId = baseUserData?.internalId

  // Read the selected conversation from the already-cached list (no extra fetch) to show its
  // title + context badges (Order / Property) in the header — mirrors the Figma thread header.
  const { data } = useGetConversations()
  const conversation = data?.content?.find((item) => item.id === selectedConversationId)

  const title = conversation ? getThreadHeaderTitle(conversation, currentUserId, t) : ''
  // Counterparty presence (best-effort, populated by the WS layer — same source the list uses).
  const otherParticipant = conversation?.participants.find((participant) => participant.userId !== currentUserId)
  const otherPresence = otherParticipant ? presence[otherParticipant.userId] : undefined
  const showSupportHours = conversation?.type === ConversationType.AGENT_CLIENT || conversation?.status === ConversationStatus.ESCALATED

  return (
    <>

      {!selectedConversationId &&
        <Stack height="100%" alignItems="center" justifyContent="center" padding="2.4rem">
          <EmptyContent
            icon={<ChatBubbleOutline />}
            title={t('chat:thread.select_title')}
            description={t('chat:thread.select_description')}
          />
        </Stack>
      }

      {!!selectedConversationId &&
        <Stack height="100%" minHeight={0}>

          {!!conversation &&
            <Stack
              gap="0.6rem"
              sx={{ padding: '1.2rem 2rem', borderBottom: `1px solid ${GRAY_200}`, backgroundColor: WHITE }}
            >

              <Stack direction="row" alignItems="center" gap="1.2rem">

                {isMobile &&
                  <IconButton
                    size="small"
                    aria-label={t('chat:actions.back')}
                    onClick={() => setActiveConversation(null)}
                    sx={{ color: GRAY_700, flexShrink: 0 }}
                  >
                    <ArrowBack />
                  </IconButton>
                }

                <Stack flex={1} minWidth={0}>

                  <Typography variant="text-lg" fontWeight={700} color={GRAY_900} noWrap>
                    {title}
                  </Typography>

                  {!!otherPresence &&
                    <Typography variant="text-xs" color={GRAY_500}>
                      {otherPresence === PresenceStatus.ONLINE ? t('chat:header.online') : t('chat:header.offline')}
                    </Typography>
                  }

                  {showSupportHours &&
                    <SupportHoursSummary />
                  }

                </Stack>

                <CloseConversationMenu conversationId={conversation.id} status={conversation.status} conversationType={conversation.type} />

              </Stack>

              {!!conversation.contexts?.length &&
                <Stack direction="row" gap="0.6rem" flexWrap="wrap">

                  {conversation.contexts.map((context) => (
                    <Typography
                      key={`${context.type}-${context.id}`}
                      variant="text-xs"
                      color={GRAY_700}
                      sx={{
                        padding: '0.2rem 0.6rem',
                        borderRadius: '0.4rem',
                        border: `1px solid ${GRAY_300}`,
                        backgroundColor: WHITE,
                      }}
                    >
                      {context.label ?? context.id}
                    </Typography>
                  ))}

                </Stack>
              }

            </Stack>
          }

          <Box flex={1} minHeight={0}>
            <ConversationThread
              conversationId={selectedConversationId}
              conversationType={conversation?.type}
              currentUserId={currentUserId}
              readOnly={conversation?.status === ConversationStatus.CLOSED}
              conversationStatus={conversation?.status}
              conversationLanguage={conversation?.language}
              reopenedAt={conversation?.reopenedAt}
            participants={conversation?.participants}
            />
          </Box>

        </Stack>
      }

    </>
  )
}
